import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Container, Col, Row } from 'reactstrap';
import moment from 'moment';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';
import 'ag-grid-enterprise';
import config from '../../../../../config';
import CardPanel from '../../../../../shared/components/widgets/CardPanel';
import DateRangePicker from '../../../../../shared/components/widgets/DateRangePicker';
import { currencyFormatter, formatNumberDefaultToZero } from '../../../../../shared/components/table/functions';

export default function FrequencyReportLayout({ title }) {
  const gridRef = useRef();
  const sellerSelect = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const theme = useSelector(store => store.theme);
  const [apiLoad, setApiLoad] = useState(false);
  const [startDate, setStartDate] = useState(moment().subtract(9, 'days')); // moment('20231005', 'YYYYMMDD')
  const [endDate, setEndDate] = useState(moment().subtract(2, 'days'));

  const [apiData, setApiData] = useState([]);

  useEffect(() => {
    setApiLoad(true);
    // setGraphData([{}]);
    setApiData([{}]);

    const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
    const requestOptions = {
      method: 'GET',
      headers: headerWithAuth,
    };

    const reportUrl = `${config.serverUrl}/amc/frequency-report?accountId=${sellerSelect.value}&startDate=${startDate.format('YYYYMMDD')}&endDate=${endDate.format('YYYYMMDD')}`;
    fetch(reportUrl, requestOptions)
      .then((results) => {
        if (!results.ok) {
          throw Error(results.statusText);
        }
        return results.json();
      }).then((data) => {
        setApiData(data);
        setApiLoad(false);
      }).catch(() => {
        setApiLoad(false);
      });
  }, [sellerSelect.value, startDate, endDate]);

  function buildMasters(rows) {
    const toNum = (v) => (v == null ? 0 : Number(v) || 0);
    // const safeDiv = (n, d) => (d ? n / d : null);

    const source = Array.isArray(rows) ? rows : [];
    const byKey = new Map();

    for (let i = 0; i < source.length; i += 1) {
      const r = source[i] || {};
      const key = `${String(r.frequency_bucket)}`;

      let group = byKey.get(key);
      if (!group) {
        group = {
          frequency_bucket: r.frequency_bucket || null,

          reach: 0,
          impressions: 0,
          cost: 0,
          clicks: 0,
          branded_searches: 0,
          detail_page_views: 0,
          add_to_carts: 0,
          purchases: 0,
          new_to_brand_purchases: 0,
          off_amazon_conversions: 0,
          pixel_conversions: 0,
          digital_subscription_signup_purchases: 0,
          units_sold: 0,
          product_sales: 0,
          new_to_brand_units_sold: 0,
          new_to_brand_product_sales: 0,
          off_amazon_conversion_value: 0,
          off_amazon_product_sales: 0,
          total_detail_page_view: 0,
          total_add_to_cart: 0,
          total_purchases: 0,
          total_units_sold: 0,
          total_product_sales: 0,
          new_to_brand_total_purchases: 0,
          new_to_brand_total_units_sold: 0,
          new_to_brand_total_product_sales: 0,

          // ofa_cpm: null,
          // ofa_cpc: null,
          // ofa_cac: null,
          // ofa_roas: null,

          daily: [],
        };
        byKey.set(key, group);
      }

      group.reach += toNum(r.reach);
      group.impressions += toNum(r.impressions);
      group.cost += toNum(r.cost);
      group.clicks += toNum(r.clicks);
      group.branded_searches += toNum(r.branded_searches);
      group.detail_page_views += toNum(r.detail_page_views);
      group.add_to_carts += toNum(r.add_to_carts);
      group.purchases += toNum(r.purchases);

      group.new_to_brand_purchases += toNum(r.new_to_brand_purchases);
      group.off_amazon_conversions += toNum(r.off_amazon_conversions);
      group.pixel_conversions += toNum(r.pixel_conversions);
      group.digital_subscription_signup_purchases += toNum(r.digital_subscription_signup_purchases);
      group.units_sold += toNum(r.units_sold);
      group.product_sales += toNum(r.product_sales);
      group.new_to_brand_units_sold += toNum(r.new_to_brand_units_sold);
      group.new_to_brand_product_sales += toNum(r.new_to_brand_product_sales);
      group.off_amazon_conversion_value += toNum(r.off_amazon_conversion_value);
      group.off_amazon_product_sales += toNum(r.off_amazon_product_sales);
      group.total_detail_page_view += toNum(r.total_detail_page_view);
      group.total_add_to_cart += toNum(r.total_add_to_cart);
      group.total_purchases += toNum(r.total_purchases);
      group.total_units_sold += toNum(r.total_units_sold);
      group.total_product_sales += toNum(r.total_product_sales);
      group.new_to_brand_total_purchases += toNum(r.new_to_brand_total_purchases);
      group.new_to_brand_total_units_sold += toNum(r.new_to_brand_total_units_sold);
      group.new_to_brand_total_product_sales += toNum(r.new_to_brand_total_product_sales);

      group.daily.push(r);
    }

    const masters = Array.from(byKey.values());

    for (let i = 0; i < masters.length; i += 1) {
      const g = masters[i];

      // g.ofa_cpm = safeDiv(g.total_ofa_spend * 1000, g.total_ofa_impressions);
      // g.ofa_cpc = safeDiv(g.total_ofa_spend, g.total_ofa_page_views); // change denominator if you have "clicks"
      // g.ofa_cac = safeDiv(g.total_ofa_spend, g.total_ofa_orders); // or use conversions if preferred
      // g.ofa_roas = safeDiv(g.total_ofa_sales, g.total_ofa_spend);

      g.daily.sort((a, b) => {
        const da = a && a.data_date ? String(a.data_date) : '';
        const db = b && b.data_date ? String(b.data_date) : '';
        if (da < db) return -1;
        if (da > db) return 1;
        return 0;
      });
    }

    masters.sort((a, b) => {
      const aNum = parseInt(a.frequency_bucket, 10);
      const bNum = parseInt(b.frequency_bucket, 10);
      if (aNum < bNum) return -1;
      if (aNum > bNum) return 1;
      return 0;
    });

    return masters;
  }

  const masters = useMemo(
    () => buildMasters(apiData || []),
    [apiData],
  );

  const masterCols = useMemo(() => [
    { field: 'frequency_bucket', headerName: 'Frequency Bucket', cellRenderer: 'agGroupCellRenderer', pinned: 'left' },
    { field: 'reach', headerName: 'Reach', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'impressions', headerName: 'Impressions', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'cost', headerName: 'Spend', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'clicks', headerName: 'Clicks', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'branded_searches', headerName: 'Branded Searches', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'detail_page_views', headerName: 'Detail Page Views', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'add_to_carts', headerName: 'Add To Carts', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'purchases', headerName: 'Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'new_to_brand_purchases', headerName: 'NTB Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'off_amazon_conversions', headerName: 'Off Amazon Conversions', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'pixel_conversions', headerName: 'Pixel Conversions', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'digital_subscription_signup_purchases', headerName: 'Digital Subscription Signup Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'units_sold', headerName: 'Units Sold', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'product_sales', headerName: 'Product Sales', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'new_to_brand_units_sold', headerName: 'NTB Units Sold', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'new_to_brand_product_sales', headerName: 'NTB Product Sales', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'off_amazon_conversion_value', headerName: 'Off Amazon Conversion Value', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'off_amazon_product_sales', headerName: 'Off Amazon Product Sales', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'total_detail_page_view', headerName: 'Total Detail Page View', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_add_to_cart', headerName: 'Total Add To Cart', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_purchases', headerName: 'Total Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_units_sold', headerName: 'Total Units Sold', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'total_product_sales', headerName: 'Total Product Sales', valueFormatter: currencyFormatter, aggFunc: 'sum' },
    { field: 'new_to_brand_total_purchases', headerName: 'NTB Total Purchases', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'new_to_brand_total_units_sold', headerName: 'NTB Total Units Sold', valueFormatter: formatNumberDefaultToZero, aggFunc: 'sum' },
    { field: 'new_to_brand_total_product_sales', headerName: 'NTB Total Product Sales', valueFormatter: currencyFormatter, aggFunc: 'sum' },
  ], []);


  const detailCellRendererParams = useMemo(() => ({
    detailGridOptions: {
      columnDefs: [
        { field: 'data_date', headerName: 'Date' },
        { field: 'reach', headerName: 'Reach', valueFormatter: formatNumberDefaultToZero },
        { field: 'impressions', headerName: 'Impressions', valueFormatter: formatNumberDefaultToZero },
        { field: 'cost', headerName: 'Spend', valueFormatter: currencyFormatter },
        { field: 'clicks', headerName: 'Clicks', valueFormatter: formatNumberDefaultToZero },
        { field: 'branded_searches', headerName: 'Branded Searches', valueFormatter: formatNumberDefaultToZero },
        { field: 'detail_page_views', headerName: 'Detail Page Views', valueFormatter: formatNumberDefaultToZero },
        { field: 'add_to_carts', headerName: 'Add To Carts', valueFormatter: formatNumberDefaultToZero },
        { field: 'purchases', headerName: 'Purchases', valueFormatter: formatNumberDefaultToZero },
        { field: 'new_to_brand_purchases', headerName: 'NTB Purchases', valueFormatter: formatNumberDefaultToZero },
        { field: 'off_amazon_conversions', headerName: 'Off Amazon Conversions', valueFormatter: formatNumberDefaultToZero },
        { field: 'pixel_conversions', headerName: 'Pixel Conversions', valueFormatter: formatNumberDefaultToZero },
        { field: 'digital_subscription_signup_purchases', headerName: 'Digital Subscription Signup Purchases', valueFormatter: formatNumberDefaultToZero },
        { field: 'units_sold', headerName: 'Units Sold', valueFormatter: formatNumberDefaultToZero },
        { field: 'product_sales', headerName: 'Product Sales', valueFormatter: currencyFormatter },
        { field: 'new_to_brand_units_sold', headerName: 'NTB Units Sold', valueFormatter: formatNumberDefaultToZero },
        { field: 'new_to_brand_product_sales', headerName: 'NTB Product Sales', valueFormatter: currencyFormatter },
        { field: 'off_amazon_conversion_value', headerName: 'Off Amazon Conversion Value', valueFormatter: currencyFormatter },
        { field: 'off_amazon_product_sales', headerName: 'Off Amazon Product Sales', valueFormatter: currencyFormatter },
        { field: 'total_detail_page_view', headerName: 'Total Detail Page View', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_add_to_cart', headerName: 'Total Add To Cart', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_purchases', headerName: 'Total Purchases', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_units_sold', headerName: 'Total Units Sold', valueFormatter: formatNumberDefaultToZero },
        { field: 'total_product_sales', headerName: 'Total Product Sales', valueFormatter: currencyFormatter },
        { field: 'new_to_brand_total_purchases', headerName: 'NTB Total Purchases', valueFormatter: formatNumberDefaultToZero },
        { field: 'new_to_brand_total_units_sold', headerName: 'NTB Total Units Sold', valueFormatter: formatNumberDefaultToZero },
        { field: 'new_to_brand_total_product_sales', headerName: 'NTB Total Product Sales', valueFormatter: currencyFormatter },
      ],
      defaultColDef: { flex: 1, sortable: true, resizable: true },
    },
    getDetailRowData: (params) => {
      params.successCallback(params.data.daily || []);
    },
  }), []);

  function dateChange(start, end) {
    setStartDate(moment(start));
    setEndDate(moment(end));
  }

  // Table Settings
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    flex: 1,
  }), []);

  const onGridReady = useCallback(() => {
    gridRef.current.api.sizeColumnsToFit({
      defaultMinWidth: 100,
    });
  }, []);

  let useTheme = 'ag-theme-balham';
  if (!theme.className.endsWith('light')) {
    useTheme = 'ag-theme-balham-dark';
  }

  const onBtnExport = () => {
    const fileName = `${sellerSelect.value}-AMC-Frequency-${moment(startDate).format('YYYYMMDD')}-${moment(endDate).format('YYYYMMDD')}.csv`;
    gridRef.current.api.exportDataAsCsv({ fileName });
  };

  return (
    <Container className="dashboard">
      <Row>
        <Col>
          <CardPanel
            lg={12}
            md={12}
            title=""
            subhead=""
            showButtons={false}
            parentRefresh={apiLoad}
          >

            <Row>
              <Col md={12}>
                <h3 className="page-title">{title}</h3>
              </Col>
            </Row>
            <Row xs={1} md={2} className="align-items-center g-3 p-3 p-md-0">
              {/* left col: center on mobile, normal on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-start text-center text-md-start">
                <DateRangePicker
                  startDate={startDate.toDate()}
                  endDate={endDate.toDate()}
                  onDateChange={dateChange}
                />
              </Col>

              {/* right col: add top padding only when stacked, center on mobile, right on md+ */}
              <Col className="d-flex justify-content-center justify-content-md-end pt-3 pt-md-0">
                <button
                  className="btn btn-outline-primary"
                  style={{ width: 200 }}
                  onClick={onBtnExport}
                  type="button"
                >
                  Export
                </button>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className={useTheme} style={{ height: '50vh', width: '100%' }}>
                  <AgGridReact
                    ref={gridRef}
                    rowData={masters}
                    columnDefs={masterCols}
                    defaultColDef={defaultColDef}
                    masterDetail
                    isRowMaster={(dataItem) => Array.isArray(dataItem?.daily) && dataItem.daily.length > 0}
                    detailCellRendererParams={detailCellRendererParams}
                    detailRowAutoHeight
                    groupSuppressAutoColumn={false}
                    animateRows
                    groupIncludeTotalFooter
                    onFirstDataRendered={(p) => p.api.forEachNode(n => n.setExpanded(false))}
                    onGridReady={onGridReady}
                  />
                </div>
              </Col>
            </Row>
          </CardPanel>
        </Col>
      </Row>
    </Container>
  );
}
